import { Component, OnInit, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FacilityGroupsService } from './facility-groups.service';
import {
  FacilityGroup,
  FacilityGroupFacility,
  CreateGroupInput,
  UpdateGroupInput,
} from './facility-groups.interface';

@Component({
  selector: 'app-facility-groups',
  templateUrl: './facility-groups.component.html',
  styleUrls: ['./facility-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FacilityGroupsComponent implements OnInit {
  groups: FacilityGroup[] = [];
  facilities: FacilityGroupFacility[] = [];
  applyInCensus = false;

  filterGroupId = 'unassigned';
  search = '';
  selectedFacilityIds = new Set<number>();
  loading = false;
  hydrated = false;

  visibleFacilities: FacilityGroupFacility[] = [];
  facilityCountByGroup: Record<string, number> = {};
  unassignedCount = 0;

  // Modal state
  modal: any = null;

  @ViewChild('rowMenuRef') rowMenuRef?: ElementRef;
  @ViewChild('chipMenuRef') chipMenuRef?: ElementRef;
  rowMenuFor: number | null = null;
  chipMenuFor: string | null = null;

  groupForm: CreateGroupInput = { name: '', description: '' };
  bulkGroupId: string | null = null;

  constructor(private service: FacilityGroupsService) {}

  get focusedGroup() {
    return this.filterGroupId !== 'unassigned'
      ? this.groups.find((g) => g._id === this.filterGroupId) || null
      : null;
  }

  getFacilityDisplayName(facility: FacilityGroupFacility): string {
    return facility.displayName || facility.name || 'Unknown Facility';
  }

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading = true;
    this.service.loadAll().subscribe({
      next: (data) => {
        this.groups = data.groups || [];
        this.facilities = data.facilities || [];
        this.applyInCensus = data.applyFacilityGroupsInCensus || false;
        this.calculateDerivedState();
        this.loading = false;
        this.hydrated = true;
      },
      error: (error) => {
        console.error('Error loading facility groups:', error);
        this.loading = false;
        this.hydrated = true;
      },
    });
  }

  calculateDerivedState() {
    // Count facilities per group
    this.facilityCountByGroup = {};
    this.unassignedCount = 0;

    for (const facility of this.facilities) {
      if (facility.currentGroup) {
        const groupId = facility.currentGroup.groupId;
        this.facilityCountByGroup[groupId] = (this.facilityCountByGroup[groupId] || 0) + 1;
      } else {
        this.unassignedCount++;
      }
    }

    this.updateVisibleFacilities();
  }

  updateVisibleFacilities() {
    const q = this.search.trim().toLowerCase();
    const matchesSearch = (f: FacilityGroupFacility) =>
      !q || f.displayName.toLowerCase().includes(q) || f.name.toLowerCase().includes(q);

    if (this.filterGroupId === 'unassigned') {
      this.visibleFacilities = this.facilities
        .filter((f) => !f.currentGroup)
        .filter(matchesSearch);
    } else {
      this.visibleFacilities = this.facilities.filter(matchesSearch);
    }
  }

  onFilterChange(groupId: string) {
    this.filterGroupId = groupId;
    this.selectedFacilityIds.clear();
    this.updateVisibleFacilities();
  }

  onSearch(value: string) {
    this.search = value;
    this.updateVisibleFacilities();
  }

  toggleFacility(facilityId: number) {
    if (this.selectedFacilityIds.has(facilityId)) {
      this.selectedFacilityIds.delete(facilityId);
    } else {
      this.selectedFacilityIds.add(facilityId);
    }
  }

  toggleAllVisible() {
    const visibleIds = this.visibleFacilities.map((f) => f.facilityId);
    const allSelected = visibleIds.every((id) => this.selectedFacilityIds.has(id));

    if (allSelected) {
      visibleIds.forEach((id) => this.selectedFacilityIds.delete(id));
    } else {
      visibleIds.forEach((id) => this.selectedFacilityIds.add(id));
    }
  }

  get allVisibleSelected(): boolean {
    return (
      this.visibleFacilities.length > 0 &&
      this.visibleFacilities.every((f) => this.selectedFacilityIds.has(f.facilityId))
    );
  }

  get selectedCount(): number {
    return this.selectedFacilityIds.size;
  }

  createGroup(input: CreateGroupInput) {
    this.service.createGroup(input).subscribe({
      next: (group) => {
        this.groups.push(group);
        this.groups.sort((a, b) => a.name.localeCompare(b.name));
        this.modal = null;
        this.onFilterChange(group._id);
      },
      error: (error) => {
        console.error('Error creating group:', error);
        alert(error.error?.error || 'Failed to create group');
      },
    });
  }

  updateGroup(id: string, input: UpdateGroupInput) {
    this.service.updateGroup(id, input).subscribe({
      next: (updated) => {
        const idx = this.groups.findIndex((g) => g._id === id);
        if (idx >= 0) {
          this.groups[idx] = updated;
          this.groups.sort((a, b) => a.name.localeCompare(b.name));
        }
        this.modal = null;
      },
      error: (error) => {
        console.error('Error updating group:', error);
        alert(error.error?.error || 'Failed to update group');
      },
    });
  }

  deleteGroup(id: string) {
    this.service.deleteGroup(id).subscribe({
      next: () => {
        this.groups = this.groups.filter((g) => g._id !== id);
        this.modal = null;
        if (this.filterGroupId === id) {
          this.onFilterChange('unassigned');
        }
        this.calculateDerivedState();
      },
      error: (error) => {
        console.error('Error deleting group:', error);
        alert(error.error?.error || 'Failed to delete group');
      },
    });
  }

  setFacilityGroup(facilityId: number, groupId: string | null) {
    this.service.setFacilityAssignment(facilityId, groupId).subscribe({
      next: () => {
        const facility = this.facilities.find((f) => f.facilityId === facilityId);
        if (facility) {
          if (groupId) {
            const group = this.groups.find((g) => g._id === groupId);
            facility.currentGroup = group
              ? { groupId: group._id, groupName: group.name }
              : null;
          } else {
            facility.currentGroup = null;
          }
        }
        this.calculateDerivedState();
      },
      error: (error) => {
        console.error('Error setting facility group:', error);
        alert(error.error?.error || 'Failed to assign facility');
      },
    });
  }

  bulkApply(groupId: string | null) {
    if (this.selectedFacilityIds.size === 0) return;

    const facilityIds = Array.from(this.selectedFacilityIds);
    const operation$ =
      groupId === null
        ? this.service.bulkUnassign(facilityIds)
        : this.service.bulkSetAssignments(facilityIds, groupId);

    operation$.subscribe({
      next: () => {
        for (const facilityId of facilityIds) {
          const facility = this.facilities.find((f) => f.facilityId === facilityId);
          if (facility) {
            if (groupId) {
              const group = this.groups.find((g) => g._id === groupId);
              facility.currentGroup = group
                ? { groupId: group._id, groupName: group.name }
                : null;
            } else {
              facility.currentGroup = null;
            }
          }
        }
        this.selectedFacilityIds.clear();
        this.modal = null;
        this.calculateDerivedState();
      },
      error: (error) => {
        console.error('Error bulk assigning:', error);
        alert(error.error?.error || 'Failed to bulk assign facilities');
      },
    });
  }

  toggleCensusApply() {
    this.service.toggleCensusApply(this.applyInCensus).subscribe({
      next: () => {
        // Already updated in the component
      },
      error: (error) => {
        console.error('Error toggling census apply:', error);
        this.applyInCensus = !this.applyInCensus; // Revert
        alert(error.error?.error || 'Failed to update setting');
      },
    });
  }

  openModal(type: string, payload?: any) {
    if (type === 'create') {
      this.groupForm = { name: '', description: '' };
    } else if (type === 'edit' && payload) {
      this.groupForm = { name: payload.name, description: payload.description };
    } else if (type === 'createAndAssign') {
      this.groupForm = { name: '', description: '' };
    } else if (type === 'bulkApply') {
      this.bulkGroupId = this.groups.length > 0 ? String(this.groups[0]._id) : null;
    }
    this.modal = { type, payload };
  }

  closeModal() {
    this.modal = null;
    this.groupForm = { name: '', description: '' };
    this.bulkGroupId = null;
  }

  onRowMenuOpen(facilityId: number) {
    this.rowMenuFor = this.rowMenuFor === facilityId ? null : facilityId;
  }

  onChipMenuOpen(groupId: string) {
    this.chipMenuFor = this.chipMenuFor === groupId ? null : groupId;
  }
}
